if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;

        /* acessórios marcados embaixo do COMPRAR (snippets/tw-acc.liquid) vão no MESMO /cart/add do produto,
           1 de cada, em JSON com items[] (o formulário só leva 1 variante). O produto vai por ÚLTIMO: a Shopify põe o
           item mais recente no topo do carrinho, e assim o produto aparece primeiro. O pixel (snippets/tw-meta-pixel)
           lê a quantidade de cada item do JSON. Se um item falhar, a Shopify não adiciona nenhum e o erro aparece aqui. */
        const addons = this.twAddons();
        if (addons.length) {
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify({
            items: addons
              .slice()
              .reverse()
              .map((id) => ({ id: Number(id), quantity: 1 }))
              .concat([{ id: Number(variantId), quantity }]),
            sections: this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : undefined,
            sections_url: this.cart ? window.location.pathname : undefined,
          });
        }

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity, addons);

        fetch(`${routes.cart_add_url}`, config)
          // resposta de erro sem JSON (429, 5xx de borda) ou sem "status" também vira erro, com o código HTTP
          .then((response) =>
            response.json().then(
              (json) => (response.ok || json.status ? json : Object.assign(json, { status: response.status })),
              () => ({ status: response.status })
            )
          )
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(this.addErrorMessage(response));
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));
              // o botão volta ao normal (a Rise trocava por "Esgotado" e travava em qualquer erro, até no 429)
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);
            // acessório já foi junto; desmarca pra um novo COMPRAR não levar outro sem querer
            this.querySelectorAll('[data-tw-addon]:checked').forEach((input) => (input.checked = false));

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(this.addErrorMessage());
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      /* texto do erro ao adicionar. 422 = regra de estoque/quantidade, que a Shopify já manda em português;
         o resto (429 de muitas tentativas, 5xx, rede) chegava em inglês ou sem texto nenhum. */
      addErrorMessage(response) {
        if (response && Number(response.status) === 422 && response.description) return response.description;
        return 'Não deu pra adicionar agora. Tenta de novo em alguns segundos.';
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      /* ids das variantes dos acessórios marcados (snippets/tw-acc.liquid). Na variante, a opção
         escolhido ([data-tw-addon-pick]) manda; sem ele, vale o value da caixinha. */
      twAddons() {
        return Array.from(this.querySelectorAll('[data-tw-addon]:checked'))
          .map((input) => {
            const row = input.closest('[data-tw-addon-row]');
            const pick = row && row.querySelector('[data-tw-addon-pick]:checked');
            return (pick && pick.value) || input.value;
          })
          .filter(Boolean);
      }

      createCartLinesUpdateEvent(variantId, quantity, addons = []) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }].concat(
              addons.map((id) => ({ merchandiseId: id, quantity: 1 }))
            ),
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
